/**
 * markings.ts — What's painted on the road, and where.
 *
 * The streets were tarmac, a centre line and a stop line, and next to a town of
 * cottages that read as unfinished: a real street carries far more paint than
 * that, and paint is the one kind of detail a street can carry **for free**.
 *
 * That is the whole argument for doing it this way. `AGENTS.md` is emphatic that
 * nothing solid may stand on or beside a footway — it pinches the one walkable
 * route, which is why there are no street trees and no bollards. Markings dodge
 * that entirely: they are flat, they have no collision, they cannot be walked
 * into, and they cost one slab each. So the street gets its character from the
 * ground up rather than from furniture standing in the player's way.
 *
 * Everything here is pure geometry over the road layout, tested in
 * `markings.test.ts`, so the renderer only has to lay slabs where it's told.
 */

/** A rectangle of paint on the ground, in world coordinates. */
export interface Marking {
  x: number;
  y: number;
  w: number;
  h: number;
}

/**
 * The stripes of a zebra crossing on one approach to a junction.
 *
 * Laid **across** the carriageway, set back from the junction square so the
 * crossing sits where the footway it joins actually is rather than in the middle
 * of the box. `orient` is the road's own orientation: a crossing on a horizontal
 * road has stripes running along x and spanning the road's width in y.
 *
 * The count is derived from the road width so the stripes stay a fixed size
 * whatever the road does, and it is forced **odd** so a stripe lands on the
 * centre line rather than a gap straddling it — the one detail that makes a
 * zebra read as a zebra rather than as a ladder dropped on the tarmac.
 */
export function zebraStripes(
  orient: string,
  along: number,
  across: number,
  roadWidth: number,
  setback: number,
  stripe: number,
  depth: number,
): Marking[] {
  const horizontal = orient === 'h';
  const pitch = stripe * 2;
  // A run of `n` stripes spans `n * pitch - stripe`, since the outer gaps aren't
  // painted — so this is the largest count whose paint still fits the road,
  // *not* the road divided by the pitch. Getting that wrong drops a stripe at
  // each end and leaves a zebra covering barely half the carriageway.
  let count = Math.floor((roadWidth + stripe) / pitch);
  if (count % 2 === 0) count -= 1; // odd, so a stripe lands on the centre line
  if (count < 1) return [];

  const span = count * pitch - stripe; // paint to paint, ignoring the outer gaps
  const out: Marking[] = [];
  for (let i = 0; i < count; i++) {
    const offset = -span / 2 + i * pitch + stripe / 2;
    // `along` is the junction centre; step out to where the crossing sits.
    const at = along + setback;
    out.push(
      horizontal
        ? { x: at, y: across + offset, w: depth, h: stripe }
        : { x: across + offset, y: at, w: stripe, h: depth },
    );
  }
  return out;
}

/**
 * A run of kerbside paint — the double yellows down each side of a road.
 *
 * Returned as one long rectangle per line rather than as dashes: these are
 * continuous, and a solid strip is one slab where a dashed one would be dozens.
 * `gap` separates the pair; `inset` is how far in from the kerb the outer line
 * sits.
 */
export function kerbLines(
  orient: string,
  pos: number,
  from: number,
  to: number,
  roadWidth: number,
  inset: number,
  gap: number,
  width: number,
): Marking[] {
  const horizontal = orient === 'h';
  const length = to - from;
  if (length <= 0) return [];
  const mid = from + length / 2;

  const out: Marking[] = [];
  for (const side of [-1, 1]) {
    for (const line of [0, 1]) {
      const across = pos + side * (roadWidth / 2 - inset - line * gap);
      out.push(
        horizontal
          ? { x: mid, y: across, w: length, h: width }
          : { x: across, y: mid, w: width, h: length },
      );
    }
  }
  return out;
}

/**
 * Where the gully drains sit along a stretch of kerb.
 *
 * Spaced like the ridge tiles on a roof are: the count is rounded and the step
 * derived back from it, so a run always starts and ends the same distance from
 * its ends instead of dribbling out short at one of them.
 */
export function drainPositions(from: number, to: number, spacing: number): number[] {
  const length = to - from;
  if (!(length > 0) || !(spacing > 0)) return [];
  const count = Math.max(1, Math.round(length / spacing));
  const step = length / count;
  const out: number[] = [];
  for (let i = 0; i < count; i++) out.push(from + step * (i + 0.5));
  return out;
}
